import {
  dayStyle, kicker, portrait, settle, t, divider, btn,
} from '../styles';
import type { WireframeKey } from '@ce/core';
import { CardArc } from '../components/Chrome';
import { PulseArc, Sheen } from '../components/Ambient';
import { Wireframe } from '../components/Wireframe';
import { CallSheet } from '../components/CallSheet';
import { PromoRotator } from '../components/PromoRotator';
import { useState } from 'react';
import {
  featuredTalent, nextUp, shortMeta, minutesUntil, findDay, navActions,
} from '@ce/core';
import type { ScreenProps } from './types';

/** Which motif sits behind each quick link. */
const WIRE_FOR: Record<string, WireframeKey> = {
  events: 'special-events',
  talent: 'photo-ops',
  'hall:exhibitors': 'exhibitor-hall',
  'hall:map': 'venue-map',
  schedule: 'schedule',
  quests: 'quests',
};

/**
 * A quick link.
 *
 * The old ring-and-dot glyph read as a crosshair and said nothing about the
 * destination. The wireframe motif does both jobs — it identifies the section
 * and gives the tile some depth — and it brightens on press.
 */
function QuickLink({
  label, note, art, index, onSelect,
}: {
  label: string; note: string; art?: WireframeKey; index: number; onSelect: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  return (
    <button
      type="button"
      onClick={onSelect}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      style={{
        position: 'relative', overflow: 'hidden', textAlign: 'left',
        padding: '14px 13px 13px', borderRadius: 10, minHeight: 92,
        border: `1px solid ${pressed ? 'var(--color-accent)' : divider}`,
        background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer',
        transform: pressed ? 'scale(0.975)' : 'none',
        transition: 'transform .16s cubic-bezier(.2,.8,.2,1), border-color .18s ease',
        ...settle(index),
      }}
    >
      {art && (
        <div
          style={{
            position: 'absolute', right: -18, bottom: -14, width: 118, height: 74,
            opacity: pressed ? 0.85 : 0.55, transition: 'opacity .2s ease',
          }}
        >
          <Wireframe art={art} strokeWidth={1.1} />
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={{ font: '500 13.5px/1.2 var(--font-body)' }}>{label}</div>
        <div style={{ fontSize: 11, color: t(0.5), marginTop: 4 }}>{note}</div>
      </div>
    </button>
  );
}

export function Home({ bundle, state, dispatch }: ScreenProps) {
  const next = nextUp(bundle, state.day);
  const featured = featuredTalent(bundle, state.interests);
  const dayEvents = bundle.sessions.filter((s) => s.day === state.day && s.track === 'Special');

  // The prototype hard-coded "18 min". Real time-to-start, with a graceful
  // fallback when the event is not currently running.
  const mins = minutesUntil(bundle, next, new Date());
  const nextIn = mins !== null && mins > 0 && mins < 600 ? `${mins} min` : findDay(bundle, next.day).dow;

  const goto = (target: string) => {
    if (target === 'hall:exhibitors') {
      dispatch({ type: 'go', screen: 'hall' });
      dispatch({ type: 'setHallTab', tab: 'exhibitors' });
    } else if (target === 'hall:map') {
      dispatch({ type: 'go', screen: 'hall' });
      dispatch({ type: 'setHallTab', tab: 'map' });
    } else {
      dispatch({ type: 'go', screen: target as never });
    }
  };

  return (
    <div className="stagger">
      <div style={{ display: 'flex', gap: 8, padding: '2px 0 16px' }}>
        {bundle.days.map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => dispatch({ type: 'setDay', day: d.key })}
            aria-pressed={state.day === d.key}
            style={dayStyle(state.day === d.key)}
          >
            <div style={{ font: '500 9px/1 var(--font-body)', letterSpacing: '0.14em', opacity: 0.65 }}>
              {d.dow}
            </div>
            <div style={{ font: '500 17px/1 var(--font-body)', marginTop: 5 }}>{d.num}</div>
          </button>
        ))}
      </div>

      <div
        style={{
          padding: '15px 16px', borderRadius: 10,
          background: 'linear-gradient(140deg, var(--color-accent-900), var(--color-surface) 70%)',
          boxShadow: 'var(--shadow-sm)', position: 'relative', overflow: 'hidden',
        }}
      >
        <Sheen delay={1.2} />
        <CardArc size={190} top={-70} right={-70} />
        <PulseArc size={190} top={-70} right={-70} opacity={0.3} delay={2} duration={7} />
        <div style={{ position: 'relative', ...kicker(9.5, 'var(--color-accent-300)') }}>
          {bundle.strings.homeNextKicker} · {nextIn}
        </div>
        <div style={{ font: '500 19px/1.2 var(--font-body)', margin: '9px 0 6px', letterSpacing: '-0.015em' }}>
          {next.title}
        </div>
        <div style={{ fontSize: 12.5, color: t(0.62) }}>{shortMeta(next)}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
          <button
            type="button"
            onClick={() => dispatch({ type: 'go', screen: 'schedule' })}
            style={btn('primary', { fontSize: 12.5 })}
          >
            Open schedule
          </button>
          <button type="button" onClick={() => goto('hall:map')} style={btn('secondary', { fontSize: 12.5 })}>
            Find the room
          </button>
        </div>
      </div>

      <PromoRotator
        bundle={bundle}
        onNavigate={(target) => navActions(target, bundle, state.day).forEach(dispatch)}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 9, marginTop: 16 }}>
        {bundle.quickLinks.map((q, i) => (
          <QuickLink key={q.label} label={q.label} note={q.note} art={WIRE_FOR[q.target]} index={i} onSelect={() => goto(q.target)} />
        ))}
      </div>

      <CallSheet
        bundle={bundle}
        cmsUrl={import.meta.env.VITE_CMS_URL ?? ''}
        day={state.day}
        interests={state.interests}
        saved={state.saved}
        onOpenSchedule={() => dispatch({ type: 'go', screen: 'schedule' })}
        onOpenSession={(id) => dispatch({ type: 'openSession', id })}
        onToggleSave={(id) => dispatch({ type: 'toggleSave', id })}
      />

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '24px 0 11px' }}>
        <div style={kicker()}>Picked for you</div>
        <button
          type="button"
          onClick={() => dispatch({ type: 'go', screen: 'talent' })}
          style={btn('ghost', { fontSize: 12 })}
        >
          All talent
        </button>
      </div>

      <div
        className="scroll"
        style={{ display: 'flex', gap: 11, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 4px' }}
      >
        {featured.map((person) => (
          <button
            key={person.id}
            type="button"
            onClick={() => dispatch({ type: 'openTalent', id: person.id })}
            style={{
              flex: 'none', width: 150, textAlign: 'left', padding: 0, border: 0,
              background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            <div
              style={{
                height: 178, borderRadius: 10, background: 'var(--color-surface)',
                position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
              }}
            >
              <div style={portrait(person.photoUrl, 22)} />
              <div
                style={{
                  position: 'absolute', inset: 0,
                  background:
                    'linear-gradient(to top, rgba(var(--color-bg-rgb), 0.92) 0%, rgba(var(--color-bg-rgb), 0.25) 42%, transparent 70%)',
                }}
              />
              <div style={{ position: 'absolute', left: 11, right: 11, bottom: 10 }}>
                <div style={{ font: '500 14px/1.2 var(--font-body)' }}>{person.name}</div>
                <div style={{ ...kicker(9, 'var(--color-accent-300)', 0.14), marginTop: 6 }}>
                  Your interests
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.4, color: t(0.52), marginTop: 9 }}>
              {person.role}
            </div>
          </button>
        ))}
      </div>

      <div style={{ ...kicker(), margin: '26px 0 11px' }}>Special events today</div>
      <div style={{ display: 'grid', gap: 9 }}>
        {dayEvents.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => dispatch({ type: 'openSession', id: s.id })}
            style={{
              display: 'flex', gap: 13, alignItems: 'center', textAlign: 'left',
              padding: '12px 14px', borderRadius: 10, border: `1px solid ${divider}`,
              background: 'transparent', color: 'var(--color-text)', cursor: 'pointer',
            }}
          >
            <div style={{ flex: 'none', width: 42 }}>
              <div style={{ font: '500 13px/1 var(--font-body)' }}>{s.t}</div>
              <div style={{ fontSize: 10, color: t(0.45), marginTop: 3 }}>{s.end}</div>
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', background: divider }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: '500 14px/1.25 var(--font-body)' }}>{s.title}</div>
              <div style={{ fontSize: 11.5, color: t(0.5), marginTop: 3 }}>{s.room}</div>
            </div>
            <div style={{ font: '500 10px/1 var(--font-body)', color: 'var(--color-accent-300)', flex: 'none' }}>
              {state.saved.includes(s.id) ? '★' : ''}
            </div>
          </button>
        ))}
      </div>
      <div style={{ height: 26 }} />
    </div>
  );
}
